/**
 * hair.js - Fließende Haare auf dem Donut
 * Haare mit mehreren Segmenten die sich bei Bewegung biegen
 */
import * as THREE from "three";
import { scene } from "./scene.js";
import { settings } from "./settings.js";

// Shader importieren
import vertexShader from "../shaders/hair.vert";
import fragmentShader from "../shaders/hair.frag";

// Hair Mesh
let hairMesh = null;
let uniforms = null;

// Für Velocity-Berechnung (pro Haar)
let lastRootPositions = null;
let hairVelocities = null;

// Attribute die jeden Frame aktualisiert werden
let rootPositionAttr = null;
let rootNormalAttr = null;
let velocityAttr = null;

// Gespeicherte Vertex-Indices
let vertexIndices = null;

// NEU: Gesampelte Wurzelpositionen und Normalen (für flächengewichtetes Sampling)
let hairRootData = null;

// Zufällige Offsets für ungeordnetes Aussehen
let hairPositionOffsets = null;
let hairNormalOffsets = null;
let hairLengthMultipliers = null;  // Unterschiedliche Längen pro Haar

// Konfiguration (kann zur Laufzeit geändert werden)
let HAIR_COUNT = 40000;           // Anzahl der Haare (dichter)
const SEGMENTS_PER_HAIR = 8;      // Segmente pro Haar (mehr für weichere Kurven)
let HAIR_LENGTH = 0.08;           // Länge der Haare
let HAIR_COLOR_ROOT = "#3D2314";  // Dunkelbraun an der Wurzel
let HAIR_COLOR_TIP = "#fff";      // Spitzenfarbe
let BEND_STRENGTH = 0.35;         // Biegestärke
let GRAVITY_STRENGTH = 0.09;      // Stärke der Schwerkraft

// Referenz zum Donut für Neuerstellen der Haare
let currentDonutMesh = null;

/**
 * Berechnet flächengewichtete Sampling-Daten für gleichmäßige Haarverteilung
 * Gibt ein Array mit kumulativen Flächenwerten und Dreiecksdaten zurück
 */
function computeAreaWeightedSampling(geometry) {
  const positions = geometry.attributes.position.array;
  const index = geometry.index;

  const triangles = [];
  let totalArea = 0;

  // Hilfsfunktion für Kreuzprodukt
  const cross = (ax, ay, az, bx, by, bz) => ({
    x: ay * bz - az * by,
    y: az * bx - ax * bz,
    z: ax * by - ay * bx
  });

  // Länge eines Vektors
  const length = (x, y, z) => Math.sqrt(x * x + y * y + z * z);

  if (index) {
    // Indexed geometry
    const indices = index.array;
    for (let i = 0; i < indices.length; i += 3) {
      const i0 = indices[i] * 3;
      const i1 = indices[i + 1] * 3;
      const i2 = indices[i + 2] * 3;

      // Dreieckspunkte
      const ax = positions[i0], ay = positions[i0 + 1], az = positions[i0 + 2];
      const bx = positions[i1], by = positions[i1 + 1], bz = positions[i1 + 2];
      const cx = positions[i2], cy = positions[i2 + 1], cz = positions[i2 + 2];

      // Kanten
      const abx = bx - ax, aby = by - ay, abz = bz - az;
      const acx = cx - ax, acy = cy - ay, acz = cz - az;

      // Fläche = 0.5 * |AB × AC|
      const crossP = cross(abx, aby, abz, acx, acy, acz);
      const area = 0.5 * length(crossP.x, crossP.y, crossP.z);

      totalArea += area;
      triangles.push({
        v0: indices[i],
        v1: indices[i + 1],
        v2: indices[i + 2],
        area,
        cumulativeArea: totalArea
      });
    }
  } else {
    // Non-indexed geometry
    const vertCount = positions.length / 3;
    for (let i = 0; i < vertCount; i += 3) {
      const i0 = i * 3;
      const i1 = (i + 1) * 3;
      const i2 = (i + 2) * 3;

      const ax = positions[i0], ay = positions[i0 + 1], az = positions[i0 + 2];
      const bx = positions[i1], by = positions[i1 + 1], bz = positions[i1 + 2];
      const cx = positions[i2], cy = positions[i2 + 1], cz = positions[i2 + 2];

      const abx = bx - ax, aby = by - ay, abz = bz - az;
      const acx = cx - ax, acy = cy - ay, acz = cz - az;

      const crossP = cross(abx, aby, abz, acx, acy, acz);
      const area = 0.5 * length(crossP.x, crossP.y, crossP.z);

      totalArea += area;
      triangles.push({
        v0: i,
        v1: i + 1,
        v2: i + 2,
        area,
        cumulativeArea: totalArea
      });
    }
  }

  return { triangles, totalArea };
}

/**
 * Sampelt einen zufälligen Punkt auf der Oberfläche mit flächengewichteter Verteilung
 * Gibt Position und interpolierte Normale zurück
 */
function sampleSurfacePoint(geometry, samplingData) {
  const { triangles, totalArea } = samplingData;
  const positions = geometry.attributes.position.array;
  const normals = geometry.attributes.normal ? geometry.attributes.normal.array : null;

  // Zufälliges Dreieck basierend auf Fläche wählen (größere Dreiecke = mehr Wahrscheinlichkeit)
  const r = Math.random() * totalArea;
  let selectedTriangle = triangles[0];

  // Binary search für Effizienz
  let low = 0, high = triangles.length - 1;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (triangles[mid].cumulativeArea < r) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  selectedTriangle = triangles[low];

  // Zufälliger Punkt innerhalb des Dreiecks (barycentric coordinates)
  let u = Math.random();
  let v = Math.random();
  if (u + v > 1) {
    u = 1 - u;
    v = 1 - v;
  }
  const w = 1 - u - v;

  const i0 = selectedTriangle.v0 * 3;
  const i1 = selectedTriangle.v1 * 3;
  const i2 = selectedTriangle.v2 * 3;

  // Interpolierte Position
  const px = positions[i0] * w + positions[i1] * u + positions[i2] * v;
  const py = positions[i0 + 1] * w + positions[i1 + 1] * u + positions[i2 + 1] * v;
  const pz = positions[i0 + 2] * w + positions[i1 + 2] * u + positions[i2 + 2] * v;

  // Interpolierte Normale
  let nx = 0, ny = 1, nz = 0;
  if (normals) {
    nx = normals[i0] * w + normals[i1] * u + normals[i2] * v;
    ny = normals[i0 + 1] * w + normals[i1 + 1] * u + normals[i2 + 1] * v;
    nz = normals[i0 + 2] * w + normals[i1 + 2] * u + normals[i2 + 2] * v;
    // Normalisieren
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len > 0) {
      nx /= len;
      ny /= len;
      nz /= len;
    }
  }

  return {
    position: { x: px, y: py, z: pz },
    normal: { x: nx, y: ny, z: nz },
    // Speichere auch einen Vertex-Index für Kompatibilität
    vertexIndex: selectedTriangle.v0
  };
}

/**
 * Erstellt die Haare
 */
export function createHair(donutMesh) {
  // Donut-Referenz speichern für späteres Neuerstellen
  currentDonutMesh = donutMesh;

  // Donut Haar-Settings (Original-Werte)
  HAIR_COUNT = 40000;
  HAIR_LENGTH = 0.08;
  HAIR_COLOR_ROOT = "#3D2314";
  HAIR_COLOR_TIP = "#fff";
  BEND_STRENGTH = 0.35;
  GRAVITY_STRENGTH = 0.09;
  console.log(`Hair: Donut mode - ${HAIR_COUNT} hairs`);

  // Falls bereits vorhanden, entfernen
  if (hairMesh) {
    scene.remove(hairMesh);
    hairMesh.geometry.dispose();
    hairMesh.material.dispose();
  }

  const donutGeometry = donutMesh.geometry;
  const vertexCount = donutGeometry.attributes.position.count;

  // Berechne flächengewichtete Sampling-Daten für gleichmäßige Verteilung
  const samplingData = computeAreaWeightedSampling(donutGeometry);
  console.log(`Hair: Computed sampling data for ${samplingData.triangles.length} triangles, total area: ${samplingData.totalArea.toFixed(4)}`);

  // Gesamtzahl der Vertices für alle Haare
  const totalVertices = HAIR_COUNT * SEGMENTS_PER_HAIR;

  // Arrays für Geometrie
  const positions = new Float32Array(totalVertices * 3);
  const rootPositions = new Float32Array(totalVertices * 3);
  const rootNormals = new Float32Array(totalVertices * 3);
  const velocities = new Float32Array(totalVertices * 3);  // Per-Haar Velocity
  const segments = new Float32Array(totalVertices);
  const randomOffsets = new Float32Array(totalVertices);
  const lengthMultipliers = new Float32Array(totalVertices);  // Längen-Variation
  const indices = [];

  // Tracking Arrays für Velocity-Berechnung
  lastRootPositions = new Float32Array(HAIR_COUNT * 3);
  hairVelocities = new Float32Array(HAIR_COUNT * 3);

  // Speichere welcher Vertex zu welchem Haar gehört
  vertexIndices = new Uint32Array(HAIR_COUNT);

  // Zufällige Offsets für ungeordnetere Haare (als Modul-Variablen speichern)
  hairPositionOffsets = new Float32Array(HAIR_COUNT * 3);
  hairNormalOffsets = new Float32Array(HAIR_COUNT * 3);
  hairLengthMultipliers = new Float32Array(HAIR_COUNT);

  // NEU: Speichere die gesampelten Positionen und Normalen direkt
  // (nicht nur Vertex-Index, da wir jetzt auf der Dreiecksfläche sampeln)
  hairRootData = [];

  for (let hair = 0; hair < HAIR_COUNT; hair++) {
    // Flächengewichtetes Sampling für gleichmäßige Verteilung
    const sample = sampleSurfacePoint(donutGeometry, samplingData);
    vertexIndices[hair] = sample.vertexIndex;

    // Speichere die exakte gesampelte Position und Normale
    hairRootData.push({
      position: sample.position,
      normal: sample.normal
    });

    const randomOffset = Math.random();

    // Kleine zufällige Position-Offsets für ungeordneteres Aussehen
    hairPositionOffsets[hair * 3] = (Math.random() - 0.5) * 0.015;
    hairPositionOffsets[hair * 3 + 1] = (Math.random() - 0.5) * 0.015;
    hairPositionOffsets[hair * 3 + 2] = (Math.random() - 0.5) * 0.015;

    // Kleine zufällige Normal-Offsets für verschiedene Richtungen
    hairNormalOffsets[hair * 3] = (Math.random() - 0.5) * 0.3;
    hairNormalOffsets[hair * 3 + 1] = (Math.random() - 0.5) * 0.3;
    hairNormalOffsets[hair * 3 + 2] = (Math.random() - 0.5) * 0.3;

    // Zufällige Länge: 0.6 bis 1.4 (also ±40% Variation)
    hairLengthMultipliers[hair] = 0.6 + Math.random() * 0.8;

    for (let seg = 0; seg < SEGMENTS_PER_HAIR; seg++) {
      const idx = hair * SEGMENTS_PER_HAIR + seg;
      const segmentRatio = seg / (SEGMENTS_PER_HAIR - 1); // 0 bis 1

      // Initiale Positionen (werden in updateHairPositions überschrieben)
      positions[idx * 3] = 0;
      positions[idx * 3 + 1] = 0;
      positions[idx * 3 + 2] = 0;

      // Root Position und Normal (werden jeden Frame aktualisiert)
      rootPositions[idx * 3] = 0;
      rootPositions[idx * 3 + 1] = 0;
      rootPositions[idx * 3 + 2] = 0;

      rootNormals[idx * 3] = 0;
      rootNormals[idx * 3 + 1] = 1;
      rootNormals[idx * 3 + 2] = 0;

      segments[idx] = segmentRatio;
      randomOffsets[idx] = randomOffset;
      lengthMultipliers[idx] = hairLengthMultipliers[hair];  // Gleiche Länge für alle Segmente eines Haars

      // Line-Indices (verbinde aufeinanderfolgende Segmente)
      if (seg < SEGMENTS_PER_HAIR - 1) {
        indices.push(idx, idx + 1);
      }
    }
  }

  // Geometrie erstellen
  const hairGeometry = new THREE.BufferGeometry();
  hairGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

  rootPositionAttr = new THREE.BufferAttribute(rootPositions, 3);
  rootPositionAttr.setUsage(THREE.DynamicDrawUsage);
  hairGeometry.setAttribute("aRootPosition", rootPositionAttr);

  rootNormalAttr = new THREE.BufferAttribute(rootNormals, 3);
  rootNormalAttr.setUsage(THREE.DynamicDrawUsage);
  hairGeometry.setAttribute("aRootNormal", rootNormalAttr);

  velocityAttr = new THREE.BufferAttribute(velocities, 3);
  velocityAttr.setUsage(THREE.DynamicDrawUsage);
  hairGeometry.setAttribute("aVelocity", velocityAttr);

  hairGeometry.setAttribute("aSegment", new THREE.BufferAttribute(segments, 1));
  hairGeometry.setAttribute("aRandomOffset", new THREE.BufferAttribute(randomOffsets, 1));
  hairGeometry.setAttribute("aLengthMult", new THREE.BufferAttribute(lengthMultipliers, 1));

  hairGeometry.setIndex(indices);

  // Uniforms (kein globales uVelocity mehr - jetzt per-Vertex)
  const gravityEnabled = settings.hairGravity ? 1.0 : 0.0;

  uniforms = {
    uTime: { value: 0 },
    uMouseY: { value: 0 },
    uHairLength: { value: HAIR_LENGTH },
    uBendStrength: { value: BEND_STRENGTH },
    uColorRoot: { value: new THREE.Color(HAIR_COLOR_ROOT) },
    uColorTip: { value: new THREE.Color(HAIR_COLOR_TIP) },
    uGravityEnabled: { value: gravityEnabled },
    uGravityStrength: { value: GRAVITY_STRENGTH },
  };

  // Shader Material
  const hairMaterial = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms,
    transparent: true,
    side: THREE.DoubleSide,
    depthWrite: false,
  });

  // LineSegments für die Haare
  hairMesh = new THREE.LineSegments(hairGeometry, hairMaterial);

  scene.add(hairMesh);

  return hairMesh;
}

/**
 * Aktualisiert die Haar-Wurzelpositionen und berechnet per-Haar Velocity
 */
function updateHairRoots(donutMesh, deltaTime) {
  if (!hairMesh || !rootPositionAttr || !hairRootData) return;

  const scale = 0.7; // Donut-Scale

  const rootPos = rootPositionAttr.array;
  const rootNorm = rootNormalAttr.array;
  const velArray = velocityAttr.array;

  // Aktuelle Geometrie-Daten (für Verformung)
  const positions = donutMesh.geometry.attributes.position.array;
  const normals = donutMesh.geometry.attributes.normal ? donutMesh.geometry.attributes.normal.array : null;

  // Donut Rotation für World-Space Transformation
  const rotY = donutMesh.rotation.y;
  const cosR = Math.cos(rotY);
  const sinR = Math.sin(rotY);

  for (let hair = 0; hair < HAIR_COUNT; hair++) {
    const rootData = hairRootData[hair];
    const vertIdx = vertexIndices[hair];

    // Hole AKTUELLE Position aus der (möglicherweise verformten) Geometrie
    // Verwende den Vertex-Index für die deformierte Position
    const currentX = positions[vertIdx * 3];
    const currentY = positions[vertIdx * 3 + 1];
    const currentZ = positions[vertIdx * 3 + 2];

    // Berechne Offset zwischen Original-Sample und nächstem Vertex
    // und wende diesen Offset auf die aktuelle Position an
    const localX = currentX * scale;
    const localY = currentY * scale;
    const localZ = currentZ * scale;

    // Zu World-Space rotieren + zufällige Position-Offsets
    const vx = localX * cosR + localZ * sinR + hairPositionOffsets[hair * 3];
    const vy = localY + hairPositionOffsets[hair * 3 + 1];
    const vz = -localX * sinR + localZ * cosR + hairPositionOffsets[hair * 3 + 2];

    // Hole AKTUELLE Normale aus der Geometrie
    let localNx, localNy, localNz;
    if (normals) {
      localNx = normals[vertIdx * 3] + hairNormalOffsets[hair * 3];
      localNy = normals[vertIdx * 3 + 1] + hairNormalOffsets[hair * 3 + 1];
      localNz = normals[vertIdx * 3 + 2] + hairNormalOffsets[hair * 3 + 2];
    } else {
      localNx = rootData.normal.x + hairNormalOffsets[hair * 3];
      localNy = rootData.normal.y + hairNormalOffsets[hair * 3 + 1];
      localNz = rootData.normal.z + hairNormalOffsets[hair * 3 + 2];
    }

    // Normalisieren nach Offset
    const nLen = Math.sqrt(localNx * localNx + localNy * localNy + localNz * localNz);
    const normLocalNx = localNx / nLen;
    const normLocalNy = localNy / nLen;
    const normLocalNz = localNz / nLen;

    const nx = normLocalNx * cosR + normLocalNz * sinR;
    const ny = normLocalNy;
    const nz = -normLocalNx * sinR + normLocalNz * cosR;

    // Per-Haar Velocity berechnen (Differenz zur letzten Position)
    const lastX = lastRootPositions[hair * 3];
    const lastY = lastRootPositions[hair * 3 + 1];
    const lastZ = lastRootPositions[hair * 3 + 2];

    const dt = Math.max(deltaTime, 0.001);
    let velX = (vx - lastX) / dt;
    let velY = (vy - lastY) / dt;
    let velZ = (vz - lastZ) / dt;

    // Velocity dämpfen für Smoothness (weniger Dämpfung = mehr Reaktivität)
    const dampFactor = 0.7;
    hairVelocities[hair * 3] = hairVelocities[hair * 3] * dampFactor + velX * (1 - dampFactor);
    hairVelocities[hair * 3 + 1] = hairVelocities[hair * 3 + 1] * dampFactor + velY * (1 - dampFactor);
    hairVelocities[hair * 3 + 2] = hairVelocities[hair * 3 + 2] * dampFactor + velZ * (1 - dampFactor);

    // Aktuelle Position speichern für nächsten Frame
    lastRootPositions[hair * 3] = vx;
    lastRootPositions[hair * 3 + 1] = vy;
    lastRootPositions[hair * 3 + 2] = vz;

    // Für jedes Segment des Haars die Daten setzen
    for (let seg = 0; seg < SEGMENTS_PER_HAIR; seg++) {
      const idx = hair * SEGMENTS_PER_HAIR + seg;

      rootPos[idx * 3] = vx;
      rootPos[idx * 3 + 1] = vy;
      rootPos[idx * 3 + 2] = vz;

      rootNorm[idx * 3] = nx;
      rootNorm[idx * 3 + 1] = ny;
      rootNorm[idx * 3 + 2] = nz;

      velArray[idx * 3] = hairVelocities[hair * 3];
      velArray[idx * 3 + 1] = hairVelocities[hair * 3 + 1];
      velArray[idx * 3 + 2] = hairVelocities[hair * 3 + 2];
    }
  }

  rootPositionAttr.needsUpdate = true;
  rootNormalAttr.needsUpdate = true;
  velocityAttr.needsUpdate = true;
}

/**
 * Aktualisiert die Haare
 */
export function updateHair(time, mouseY, donutMesh, deltaTime) {
  if (!uniforms || !donutMesh) return;

  uniforms.uTime.value = time;
  uniforms.uMouseY.value = mouseY;

  // Haar-Wurzeln und per-Haar Velocities aktualisieren
  updateHairRoots(donutMesh, deltaTime);
}

/**
 * Synchronisiert Haar-Rotation mit Donut
 * HINWEIS: Nicht mehr benötigt - Rotation wird jetzt in updateHairRoots gehandhabt
 */
export function syncHairRotation(donutMesh) {
  // Bewusst leer - Rotation wird jetzt manuell in World-Space berechnet
  // um Drift-Probleme zu vermeiden
}

/**
 * Setzt die Haar-Farben
 * @param {string|null} rootColor - Wurzelfarbe (null = nicht ändern)
 * @param {string|null} tipColor - Spitzenfarbe (null = nicht ändern)
 */
export function setHairColors(rootColor, tipColor) {
  console.log("[Hair] setHairColors called:", { rootColor, tipColor, hasUniforms: !!uniforms });
  if (uniforms) {
    if (rootColor) {
      uniforms.uColorRoot.value = new THREE.Color(rootColor);
      console.log("[Hair] Set root color:", rootColor);
    }
    if (tipColor) {
      uniforms.uColorTip.value = new THREE.Color(tipColor);
      console.log("[Hair] Set tip color:", tipColor);
    }
  }
}

/**
 * Setzt die Haar-Länge
 */
export function setHairLength(length) {
  if (uniforms) {
    uniforms.uHairLength.value = length;
  }
}

/**
 * Setzt die Biegestärke
 */
export function setBendStrength(strength) {
  if (uniforms) {
    uniforms.uBendStrength.value = strength;
  }
}

/**
 * Aktiviert/Deaktiviert den Schwerkraft-Modus
 * Haare fallen nach unten wie bei Mensch/Hund
 */
export function setGravityEnabled(enabled) {
  settings.hairGravity = enabled;
  if (uniforms) {
    uniforms.uGravityEnabled.value = enabled ? 1.0 : 0.0;
  }
}

/**
 * Setzt die Schwerkraft-Stärke
 */
export function setGravityStrength(strength) {
  if (uniforms) {
    uniforms.uGravityStrength.value = strength;
  }
}

/**
 * Setzt die Anzahl der Haare und erstellt sie neu
 * ACHTUNG: Teuer! Erstellt die gesamte Geometrie neu
 */
export function setHairCount(count) {
  HAIR_COUNT = Math.max(1000, Math.min(count, 100000)); // Limit: 1000-100000
  if (currentDonutMesh) {
    // Aktuelle Farben speichern (falls schon gesetzt)
    const savedRootColor = uniforms ? uniforms.uColorRoot.value.getHexString() : null;
    const savedTipColor = uniforms ? uniforms.uColorTip.value.getHexString() : null;

    createHair(currentDonutMesh);

    // Farben wieder anwenden (falls vorher gesetzt)
    if (savedRootColor) {
      uniforms.uColorRoot.value.setHex(parseInt(savedRootColor, 16));
    }
    if (savedTipColor) {
      uniforms.uColorTip.value.setHex(parseInt(savedTipColor, 16));
    }
  }
}
